// Redux slice for tworobots.com account auth (thirdweb login -> backend JWTs).
//
// Token persistence uses the SAME localStorage keys as the game client so the
// two stay consistent if they ever share an origin:
//   jwtToken     -> access token
//   refreshToken -> refresh token
//   user         -> JSON user object
import { createSlice } from '@reduxjs/toolkit'

export const ACCESS_TOKEN_KEY = 'jwtToken'
export const REFRESH_TOKEN_KEY = 'refreshToken'
export const USER_KEY = 'user'

function readStorage() {
  if (typeof window === 'undefined') {
    return { accessToken: null, refreshToken: null, user: null }
  }
  let user = null
  try {
    const raw = localStorage.getItem(USER_KEY)
    user = raw ? JSON.parse(raw) : null
  } catch {
    user = null
  }
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
    user
  }
}

function persist({ accessToken, refreshToken, user }) {
  if (typeof window === 'undefined') return
  if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
}

function clearStorage() {
  if (typeof window === 'undefined') return
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  // Legacy/duplicate key the game client also writes.
  localStorage.removeItem('authToken')
}

const getInitialState = () => {
  const { accessToken, refreshToken, user } = readStorage()
  return {
    isAuthenticated: Boolean(accessToken && user),
    isLoading: false,
    user,
    accessToken,
    refreshToken,
    error: null
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    authStart: state => {
      state.isLoading = true
      state.error = null
    },
    loginSuccess: (state, action) => {
      const { user, accessToken, refreshToken } = action.payload
      state.isAuthenticated = true
      state.isLoading = false
      state.user = user
      state.accessToken = accessToken
      state.refreshToken = refreshToken
      state.error = null
      persist({ accessToken, refreshToken, user })
    },
    // Rotate tokens after a refresh without touching auth status.
    tokensRefreshed: (state, action) => {
      const { accessToken, refreshToken, user } = action.payload
      if (accessToken) state.accessToken = accessToken
      if (refreshToken) state.refreshToken = refreshToken
      if (user) state.user = user
      persist({ accessToken, refreshToken, user })
    },
    setUser: (state, action) => {
      state.user = action.payload
      persist({ user: action.payload })
    },
    authError: (state, action) => {
      state.isLoading = false
      state.error = action.payload || 'Login failed'
    },
    // Re-read persisted tokens (e.g. on hard reload / cross-tab sync).
    rehydrate: state => {
      const { accessToken, refreshToken, user } = readStorage()
      state.accessToken = accessToken
      state.refreshToken = refreshToken
      state.user = user
      state.isAuthenticated = Boolean(accessToken && user)
    },
    logout: state => {
      state.isAuthenticated = false
      state.isLoading = false
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      state.error = null
      clearStorage()
    }
  }
})

export const {
  authStart,
  loginSuccess,
  tokensRefreshed,
  setUser,
  authError,
  rehydrate,
  logout
} = authSlice.actions

export default authSlice.reducer
